import React, { useState, useEffect, useContext } from 'react';
import { ask__ } from './../../shared/utils/i18n.js';
import { scrollWebsiteToAppStart } from './../../shared/utils/environment.js';
import { getGoogleAnalyticsAcccount } from './../../shared/utils/analytics.js';
import { reserveAppointment, addShoppingCart, findClient } from './../../shared/utils/api.js';
import ReactGA from 'react-ga';

import Timeslot from '../Timeslot/Timeslot';
import ReactModal from 'react-modal';
import Icon from '../Icon/Icon';
import Login from '../Login/Login';
import Loader from '../Loader/Loader';
import Register from '../Register/Register';

import { GlobalErrorContext } from './../../shared/context/GlobalErrorContext.js';
import { GlobalUserContext } from '../../shared/context/GlobalUserContext.js';
import { GlobalAppointmentContext } from '../../shared/context/GlobalAppointment.js';
import { GlobalIsRegisteredContext } from '../../shared/context/GlobalIsRegistered.js';

import "./ConfirmAppointment.scss";

export default function ConfirmAppointment() {

  const {setGlobalError} = useContext(GlobalErrorContext);
  const {getGlobalUser} = useContext(GlobalUserContext);
  const {getGlobalAppointment, setGlobalAppointment} = useContext(GlobalAppointmentContext);
  const {getGlobalIsRegistered} = useContext(GlobalIsRegisteredContext);

  const [title, setTitle] = useState(ask__('Booking: Confirm appointment'));
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const [phase, setPhase] = useState(1);
  const [refreshPhase, setRefreshPhase] = useState(0);
  const [getNotes, setNotes] = useState('');

  // 1st render => save selected on sessionStorage
  useEffect(() => {

    scrollWebsiteToAppStart();

    if (getGlobalAppointment.hasConfirmation) {
      (async () => {
        try {
          await addShoppingCart({
            'time': getGlobalAppointment.timeStart,
            'type': getGlobalAppointment.type,
            'resource': getGlobalAppointment.resourcePath,
            'undo': false,
          });
        } catch(err) {
          console.error(err);
        }
      })();
    }

  }, []);

  // flush selected appointment on close
  useEffect(() => {
    if (!isOpen) {

      // remove from shopping cart
      if (getGlobalAppointment.hasConfirmation) {
        (async () => {
          try {
            await addShoppingCart({
              'time': getGlobalAppointment.timeStart,
              'type': getGlobalAppointment.type,
              'resource': getGlobalAppointment.resourcePath,
              'undo': true,
            });
          } catch(err) {
            console.error(err);
          }
        })();
      }

      window.sessionStorage.removeItem('oa-booking-confirmation');
      setGlobalAppointment({hasConfirmation: false});
    }
  }, [isOpen]);

  // set phase 1-4
  useEffect(() => {

    setIsValidating(true);

    (async () => {

      if (!getGlobalUser.isLoggedIn) {
        setPhase(1);
        setIsValidating(false);
      } else if (getGlobalUser.isLoggedIn && !getGlobalIsRegistered) {
        setPhase(2);
        setIsValidating(false);
      } else {

        const validUserData = await hasValidClientDetails();

        if (!validUserData) {
          setPhase(2);
          setIsValidating(false);
        } else {
          if (getGlobalUser.isLoggedIn && getGlobalIsRegistered && !isConfirmed) {
            setPhase(3);

            setIsValidating(false);
          }
          if (getGlobalUser.isLoggedIn && getGlobalIsRegistered && isConfirmed) {
            setPhase(4);
            setIsValidating(false);
          }
        }

      }

    })();

  }, [isConfirmed, getGlobalUser, getGlobalIsRegistered, refreshPhase]);

  // confirmation done
  useEffect(() => {

    if (isConfirmed) {

      setTitle(ask__('Booking: Appointment confirmed'));
      window.sessionStorage.removeItem('oa-booking-confirmation');

      // track Google Analytics
      ReactGA.initialize(getGoogleAnalyticsAcccount());
      ReactGA.event({
        category: 'Nettiajanvaraus',
        action: 'Varaus',
        label: 'Vahvista varaus',
        nonInteraction: true
      });
      console.log('Tracking: Confirmation tracked.');

    } else {
      setTitle(ask__('Booking: Confirm appointment'));
    }

  }, [isConfirmed]);

  /**
   * Validate that client has required info before proceeding
   *
   * There can be old users with missing info
   */
  const hasValidClientDetails = async () => {

    const client = await findClient();
    if (client.firstName && client.firstName.length > 0 &&
        client.lastName && client.lastName.length > 0 &&
        client.phone && client.phone.length > 0 &&
        client.postcode && client.postcode.length > 0 &&
        client.city && client.city.length > 0 &&
        client.address && client.address.length > 0
      ) {

      console.log('✅ Client details validated', client);
      return true;
    }

    console.log('❌ Client details not valid', client);
    return false;

  };

  const handleConfirmationSubmit = (e) => {

    e.preventDefault();
    setIsLoading(true);


    (async () => {

      const payload = {
        'start': getGlobalAppointment.timeStart,
        'unit': getGlobalAppointment.unit,
        'duration': getGlobalAppointment.duration,
        'resource': getGlobalAppointment.resourcePath,
        'type': getGlobalAppointment.type,
        'notes': getNotes,
      };

      try {
        setIsConfirmed(false);
        const reservation = await reserveAppointment(payload);
        if (reservation.status == 200 && reservation.data.start && reservation.data.client && reservation.data.resource) {
          setIsConfirmed(true);
        } else {
          setGlobalError({
            isError: true,
            errorCode: 'RE105'
          });
          setIsConfirmed(false);
        }

      } catch(err) {
        console.error(err);
        setGlobalError({
          isError: true,
          errorCode: 'RE105'
        });
        setIsConfirmed(false);
      }

      setTimeout(() => {
        setIsLoading(false);
      }, 100);

    })();



  };


  // window.top.innerHeight
  // window.top.innerWidth

  return (
    <ReactModal
      isOpen={isOpen}
      // style={styles}
      onRequestClose={() => setIsOpen(false)}
    >
      <button
        className="modal-close"
        onClick={e => {
          e.preventDefault();
          setIsOpen(false);
        }}
      >
        <Icon name="close" />
      </button>

      <h2 className="modal-title">{title}</h2>

      {getGlobalAppointment.notice ? <>
        <div className="confirmation__notice">
          <div className="confirmation__notice__icon"><Icon name="alert-outline" /></div>
          <div className="confirmation__notice__content" dangerouslySetInnerHTML={{__html: (getGlobalAppointment.notice || '')}}></div>
        </div>
      </> : ''}

      <div className="confirmation-layout">

        <div className="confirmation-layout__info">
          <Timeslot
            type="confirmation"
            phase={phase}
            timeStart={getGlobalAppointment.timeStart}
            timeDay={getGlobalAppointment.timeDay}
            timeHour={getGlobalAppointment.timeHour}
            duration={getGlobalAppointment.duration}
            unit={getGlobalAppointment.unit}
            resourcePath={getGlobalAppointment.resourcePath}
            isVideoReception={getGlobalAppointment.isVideoReception}
            isPhoneReception={getGlobalAppointment.isPhoneReception}
            name={getGlobalAppointment.name}
            title={getGlobalAppointment.title}
            legal={getGlobalAppointment.legal}
            image={getGlobalAppointment.image}
            ninchatId={getGlobalAppointment.ninchat_id}
            locationName={getGlobalAppointment.locationName}
            locationAddress={getGlobalAppointment.locationAddress}
          />
        </div>

        {phase == 1 || phase == 2 && !isValidating ? <>

          <div className="confirmation-layout__actions">
            {getGlobalUser.isLoggedIn && getGlobalIsRegistered ? <>
              <Register
                type="edit"
                onSave={() => {
                  // force check is phase can be changed
                  setRefreshPhase(Date.now());
                }}
              />
            </> : <>
              <Login
                type="confirmation"
              />
            </>}
          </div>

        </> : <>

          {getGlobalAppointment.hasConfirmation && (phase == 3 || phase == 4) ? <>

            <div className="confirmation-layout__actions">

              <h2>{ ask__('Booking: Final phase') }</h2>

              {!isConfirmed && !isLoading && !isValidating ? <>

                <div className="confirmation-terms" dangerouslySetInnerHTML={{__html: (window.ortonWebsiteData.i18n.cancel || '')}}></div>

                <div className="confirmation-notes">
                  <label className="confirmation-notes__label">
                    <p className="confirmation-notes__instructions">{ ask__('Booking: Notes instruction')}</p>
                    <textarea
                      className="confirmation-notes__textarea"
                      value={getNotes}
                      onChange={e => {
                        setNotes(e.target.value);
                      }}
                      maxLength={750}
                      rows={3}
                    />
                  </label>
                </div>

                <div className="cta-confirmation">
                  <button onClick={handleConfirmationSubmit}>
                    {ask__('Booking: Reserve button')}
                  </button>
                </div>

              </> : ''}

              {isConfirmed && !isLoading && !isValidating ? <>

                <div className="confirmation-success">
                  <div className="confirmation-success__icon">
                    <Icon name="check-outline" />
                  </div>
                  <p>{ask__('Booking: Successful booking')}</p>
                </div>

                <div className="confirmation-close">
                  <button
                    onClick={e => {
                      e.preventDefault();
                      setIsOpen(false);
                    }
                  }>{ask__('Booking: Close confirmation')}</button>
                </div>

              </> : ''}

              {isLoading || isValidating ? <>
                <div className="confirmation-layout__actions">
                  <Loader />
                </div>
              </> : ''}

            </div>

          </> : ''}

        </>}

      </div>

    </ReactModal>
  )

}
